import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "4",
  "slug": "multiplayer-lobbies-are-here",
  "title": "Multiplayer Lobbies Are Here",
  "excerpt": "Challenge your friends in real time. Create a lobby, share the six-letter code, and see who has the best read of a satellite frame.",
  "date": "2026-02-28",
  "category": "Updates",
  "image": "https://images.unsplash.com/photo-1569429593410-b498b3fb3387?w=800&q=80",
  "readTime": "3 min",
  "content": [
    {
      "type": "paragraph",
      "text": "For a long time EarthGuessr was a single-player game. You played alone, you compared scores on a leaderboard, you maybe texted a friend a screenshot. That works, but it never quite captured the thing that makes geography games actually fun in the room — three people staring at the same satellite frame, arguing about whether the road network looks more like Croatia or Slovenia. We wanted that. Multiplayer Lobbies make it possible."
    },
    {
      "type": "heading",
      "text": "How a lobby works"
    },
    {
      "type": "paragraph",
      "text": "Anyone with an account can create a lobby. When you do, the server generates a six-character join code (uppercase letters and digits, with the visually ambiguous characters like I, O, 0, and 1 deliberately excluded). You share that code with anyone you want to play with — by text message, on a video call, on a sticky note. They open EarthGuessr, click \"join lobby\", type the code, and they are in."
    },
    {
      "type": "paragraph",
      "text": "No friend list, no accepting invitations, no waiting for someone to be \"online\". The code is the entire access pattern. Lobbies are also not password-protected, but they do not need to be — six characters with the ambiguous ones removed gives roughly 1.7 billion combinations, far more than anyone would brute-force for the privilege of joining your geography game."
    },
    {
      "type": "heading",
      "text": "What happens once everyone is in"
    },
    {
      "type": "paragraph",
      "text": "The host sees a player list that updates in real time as people join. When you are ready, the host starts the game. From that moment everyone in the lobby sees the same five satellite frames, in the same order, with the same time limit per round. You all guess independently — there is no live cursor stealing — but every round ends together and every result screen shows where each player guessed and how they scored."
    },
    {
      "type": "paragraph",
      "text": "That synchronisation is the whole feature. Without it, multiplayer would just be \"we are all playing the game and we will compare at the end\". With it, multiplayer becomes a shared event — you watch your friend's guess pin land 4,000 km off the actual location and you get to mercilessly take the lead."
    },
    {
      "type": "heading",
      "text": "Two lobby types"
    },
    {
      "type": "paragraph",
      "text": "When you create a lobby you pick between two formats. Classic is five rounds, best total score wins. Knockout is health-points-style — every player starts with 5,000 HP and loses some after each round depending on how close their guess was. Run out of HP and you are eliminated. The last player standing wins. Knockout takes longer than classic and rewards different play; classic is fastest if you have ten minutes, knockout is more dramatic if you have twenty."
    },
    {
      "type": "heading",
      "text": "How real-time works"
    },
    {
      "type": "paragraph",
      "text": "Multiplayer is built on Supabase Realtime, which is a managed channel system on top of Postgres replication and WebSockets. Each lobby has two channels — one for the player list and one for game state. When someone joins, leaves, submits a guess, or the host starts the round, the message is broadcast to every connected client through that lobby's channels."
    },
    {
      "type": "paragraph",
      "text": "We did not write a custom WebSocket server, and we did not try to roll our own peer-to-peer connection. The whole real-time piece is a thin layer on top of the same database we use for everything else. That has made multiplayer remarkably stable — if a player drops their phone in their lap mid-game, the channel reconnects and they pick up where they left off."
    },
    {
      "type": "heading",
      "text": "A few small details"
    },
    {
      "type": "list",
      "items": [
        "You do need an account to create a lobby. Joining a lobby works without one, so you can pull a friend in even if they have never played.",
        "The host can leave a lobby — the game just ends. We do not currently transfer host status to another player, mostly because it has not been needed yet.",
        "Default lobby size is large enough for casual group play. You will run out of friends before you run out of slots.",
        "Lobbies are ephemeral. Once a game finishes, the lobby is closed. To play another round, the host creates a new lobby and shares a new code. This is the simplest possible model and it has worked well so far."
      ]
    },
    {
      "type": "paragraph",
      "text": "Multiplayer Lobbies are live now. Create one, share the code, and find out which of your friends has actually been paying attention in geography class."
    }
  ]
};
